import React, { useMemo } from "react";
import { Box, Typography, Select, MenuItem, FormControl } from "@mui/material";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Area,
  ComposedChart,
} from "recharts";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { ValueLeakageTrend as ValueLeakageTrendType } from "../../../types/contractDashboard";
import styles from "./valueLeakageTrend.module.scss";

interface ValueLeakageTrendProps {
  data: ValueLeakageTrendType[];
}

const ValueLeakageTrend: React.FC<ValueLeakageTrendProps> = ({ data }) => {
  // Memoize the formatted chart data
  const chartData = useMemo(() => {
    return data.map((item) => ({
      month: item.month,
      contracted: item.contractedPrice,
      actualPaid: item.actualPaid,
      leakage: item.actualPaid - item.contractedPrice,
    }));
  }, [data]);

  // Memoize domain calculation for Y-axis
  const yAxisDomain = useMemo(() => {
    if (chartData.length === 0) return [0, 100];
    const allValues = chartData.flatMap((item) => [
      item.contracted,
      item.actualPaid,
    ]);
    const min = Math.min(...allValues);
    const max = Math.max(...allValues);
    const padding = (max - min) * 0.1;
    return [Math.floor(min - padding), Math.ceil(max + padding)];
  }, [chartData]);

  // Format currency for display
  const formatCurrency = (value: number) => {
    if (value >= 1000000) {
      return `$${(value / 1000000).toFixed(1)}M`;
    } else if (value >= 1000) {
      return `$${(value / 1000).toFixed(0)}K`;
    }
    return `$${value.toFixed(0)}`;
  };

  // Custom tooltip component
  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <Box className={styles.tooltip}>
          <Typography variant="body2" className={styles.tooltipMonth}>
            {label}
          </Typography>
          <Box className={styles.tooltipContent}>
            <Box className={styles.tooltipRow}>
              <Box
                className={styles.tooltipDot}
                style={{ background: "#08ADC2" }}
              />
              <Typography variant="body2" className={styles.tooltipLabel}>
                Contracted:
              </Typography>
              <Typography variant="body2" className={styles.tooltipValue}>
                {formatCurrency(data.contracted)}
              </Typography>
            </Box>
            <Box className={styles.tooltipRow}>
              <Box
                className={styles.tooltipDot}
                style={{ background: "#ef4444" }}
              />
              <Typography variant="body2" className={styles.tooltipLabel}>
                Actual Paid:
              </Typography>
              <Typography variant="body2" className={styles.tooltipValue}>
                {formatCurrency(data.actualPaid)}
              </Typography>
            </Box>
            <Box className={styles.tooltipDivider} />
            <Box className={styles.tooltipRow}>
              <Typography
                variant="body2"
                className={styles.tooltipLeakageLabel}
              >
                Leakage:
              </Typography>
              <Typography
                variant="body2"
                className={styles.tooltipLeakageValue}
                style={{
                  color: data.leakage > 0 ? "#ef4444" : "#10b981",
                }}
              >
                {data.leakage > 0 ? "+" : ""}
                {formatCurrency(Math.abs(data.leakage))}
              </Typography>
            </Box>
          </Box>
        </Box>
      );
    }
    return null;
  };

  // Custom legend
  const renderLegend = () => {
    return (
      <Box className={styles.legend}>
        <Box className={styles.legendItem}>
          <Box
            className={styles.legendLine}
            style={{ background: "#08ADC2" }}
          />
          <Typography variant="body2" className={styles.legendLabel}>
            Contracted Price
          </Typography>
        </Box>
        <Box className={styles.legendItem}>
          <Box
            className={styles.legendLine}
            style={{ background: "#ef4444" }}
          />
          <Typography variant="body2" className={styles.legendLabel}>
            Actual Paid
          </Typography>
        </Box>
      </Box>
    );
  };

  if (!data || data.length === 0) {
    return (
      <Box className={styles.container}>
        <Box className={styles.header}>
          <Box className={styles.titleSection}>
            <Typography variant="h6" className={styles.title}>
              Value Leakage Trend
            </Typography>
            <Typography variant="body2" className={styles.subtitle}>
              Difference between contracted price and paid price.
            </Typography>
          </Box>
        </Box>
        <Box className={styles.emptyState}>
          <Typography variant="body2" className={styles.emptyText}>
            No leakage trend data available
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box className={styles.container}>
      <Box className={styles.header}>
        <Box className={styles.titleSection}>
          <Typography variant="h6" className={styles.title}>
            Value Leakage Trend
          </Typography>
          <Typography variant="body2" className={styles.subtitle}>
            Difference between contracted price and paid price.
          </Typography>
        </Box>
        <FormControl className={styles.filter}>
          <Select
            defaultValue="Last 6 months"
            IconComponent={KeyboardArrowDownIcon}
            className={styles.select}
          >
            <MenuItem value="Last 6 months">Last 6 months</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {renderLegend()}

      <Box className={styles.chartContainer}>
        <ResponsiveContainer width="100%" height={300}>
          <LineChart
            data={chartData}
            margin={{ top: 10, right: 30, left: 20, bottom: 10 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
            <XAxis
              dataKey="month"
              tick={{ fill: "#8ea39a", fontSize: 12 }}
              axisLine={{ stroke: "#e0e0e0" }}
              tickLine={false}
            />
            <YAxis
              domain={yAxisDomain}
              tickFormatter={formatCurrency}
              tick={{ fill: "#8ea39a", fontSize: 12 }}
              axisLine={{ stroke: "#e0e0e0" }}
              tickLine={false}
            />
            <Tooltip content={<CustomTooltip />} />
            <Line
              type="monotone"
              dataKey="contracted"
              stroke="#08ADC2"
              strokeWidth={2}
              dot={{ fill: "#08ADC2", r: 4 }}
              activeDot={{ r: 6, fill: "#08ADC2" }}
              animationDuration={800}
            />
            <Line
              type="monotone"
              dataKey="actualPaid"
              stroke="#ef4444"
              strokeWidth={2}
              dot={{ fill: "#ef4444", r: 4 }}
              activeDot={{ r: 6, fill: "#ef4444" }}
              animationDuration={800}
            />
          </LineChart>
        </ResponsiveContainer>
      </Box>
    </Box>
  );
};

export default React.memo(ValueLeakageTrend);
